/**
 * The renderer: everything on the page's canvas is drawn with WebGL, in two
 * ways.
 *
 *   - The heavy layers (the planet, the coastline, every town, the glow of
 *     the big cities, the settled lights and the supernova's particles) run
 *     on the GPU as whole buffers, moved only by a handful of numbers a frame.
 *   - Everything else (the star, the sun, the network, the places) draws
 *     through `ctx`, the 2D stand-in, exactly as it would on a 2D canvas.
 *
 * A frame is `begin`, any mix of layers and `ctx` calls in painting order,
 * then `end`. The viewport and the light are given to `begin` as values; the
 * renderer keeps no other state about the page.
 */
import { StandIn, type Canvas2D } from './canvas2d'
import { createGpu, type Gpu, type Viewport } from './gpu'
import {
  createLayers,
  type Layers,
  type Light,
  type Motion,
  type PlanetLook,
  type View,
} from './layers'
import {
  buildGlobe,
  buildParticles,
  type GlobeResources,
  type ParticleResources,
  type Particles,
} from './resources'

export type { Canvas2D } from './canvas2d'
export type { Light, Motion, PlanetLook, View } from './layers'
export type { Particles } from './resources'

/** What a frame is drawn at: the viewport, and where the backlight comes from. */
export interface Frame extends Viewport {
  readonly light: Light
}

/** A point on the unit globe. */
export interface GlobePoint {
  readonly x: number
  readonly y: number
  readonly z: number
}

export interface Renderer {
  /** The 2D stand-in that the star, the sun, the network and the places draw through. */
  readonly ctx: Canvas2D
  /** Sets WebGL up on the canvas; false when this browser has none, and nothing can be drawn. */
  init(): boolean
  /** Starts a frame: clears the canvas to the night, and resets the stand-in. */
  begin(frame: Frame): void
  /** Ends a frame, drawing whatever the stand-in still holds. */
  end(): void
  /** Takes freshly seeded particles, for the supernova and the settled lights. */
  setParticles(particles: Particles): void
  /** The places whose lights are boosted, with how much; up to five are used. */
  setNear(places: [GlobePoint, number][]): void
  /** The shade: how much the lights dim towards the copy's side (`w`: + right, - left), and where on screen (px) they are out. */
  setShade(w: number, x?: number): void
  planet(v: View, look: PlanetLook): void
  towns(v: View, alpha: number, dayK: number): void
  metros(v: View, alpha: number, dayK: number): void
  lights(v: View, tau: number, dayK: number, backK: number): void
  sparks(tau: number, trail: number, dayK: number): void
  grain(alpha: number): void
}

const NIGHT = [8 / 255, 9 / 255, 11 / 255] as const
const MAX_NEAR = 5

export function createRenderer(canvas: HTMLCanvasElement, motion: Motion): Renderer {
  let gpu: Gpu | null = null
  let layers: Layers | null = null
  let globe: GlobeResources | null = null
  let ok = false
  let particles: Particles | null = null
  let particleResources: ParticleResources | null = null
  let particlesDirty = false
  const state = {
    viewport: { width: 0, height: 0, dpr: 1 },
    light: { lx: 1, ly: 0, ce: 1, se: 0 },
    near: new Float32Array(MAX_NEAR * 4),
    shade: { w: 0, x: 0 },
  }
  const standIn = new StandIn(ready)

  return {
    ctx: standIn,
    init() {
      gpu = createGpu(canvas, setup)
      ok = gpu !== null
      if (ok) {
        layers = createLayers(gpu!, motion)
        try {
          setup()
        } catch (e) {
          console.error(e)
          ok = false
        }
      }
      return ok
    },
    begin(frame) {
      const { light, ...viewport } = frame
      state.viewport = viewport
      state.light = light
      standIn.begin(viewport)
      if (!ready()) return
      const gl = gpu!.gl
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.clearColor(NIGHT[0], NIGHT[1], NIGHT[2], 1)
      gl.clear(gl.COLOR_BUFFER_BIT)
    },
    end() {
      standIn.flush()
    },
    setParticles(p) {
      particles = p
      particlesDirty = true
    },
    setNear(places) {
      state.near.fill(0)
      places.slice(0, MAX_NEAR).forEach(([point, boost], i) => {
        state.near.set([point.x, point.y, point.z, boost], i * 4)
      })
    },
    setShade(w, x) {
      state.shade = { w, x: x || 0 }
    },
    planet(v, look) {
      if (!ready()) return
      standIn.flush()
      layers!.planet(globe!, v, look, state)
    },
    towns(v, alpha, dayK) {
      if (!ready() || alpha <= 0) return
      standIn.flush()
      layers!.towns(globe!, v, alpha, dayK, state)
    },
    metros(v, alpha, dayK) {
      if (!ready() || alpha <= 0) return
      standIn.flush()
      layers!.metros(globe!, v, alpha, dayK, state)
    },
    lights(v, tau, dayK, backK) {
      if (!ready() || !particles) return
      standIn.flush()
      const count = particles.shell.count
      layers!.lights(globe!, particleBuffers(), count, v, tau, dayK, backK, state)
    },
    sparks(tau, trail, dayK) {
      if (!ready() || !particles) return
      standIn.flush()
      const count = particles.shell.count + particles.ejecta.count
      layers!.sparks(globe!, particleBuffers(), count, tau, trail, dayK, state)
    },
    grain(alpha) {
      if (!ready() || alpha <= 0) return
      standIn.flush()
      layers!.grain(globe!, alpha, state)
    },
  }

  /** The particles' buffers, rebuilt if they were reseeded. */
  function particleBuffers() {
    if (particlesDirty) {
      particleResources = buildParticles(gpu!, particles!, particleResources)
      particlesDirty = false
    }
    return particleResources!
  }

  /** Builds everything on the GPU: at init, and again when a lost context is restored. */
  function setup() {
    gpu!.reset()
    standIn.rebuild(gpu!)
    globe = buildGlobe(gpu!)
    particleResources = null
    particlesDirty = true
  }

  function ready() {
    return ok && !gpu!.lost()
  }
}
